import { getState, patchState, signalStoreFeature, SignalStoreFeatureResult, watchState, withHooks, withMethods } from '@ngrx/signals';

type SelectFn<State> = (state: State) => Record<string, unknown>;
type ParseFn<State> = (data: object) => Partial<State>;

export function withWebStorageSync<Input extends SignalStoreFeatureResult>(config: {
  storage: Storage;
  prefix: string;
  props?: string[];
  select?: SelectFn<Input['state']>;
  parse?: ParseFn<Input['state']>;
}) {
  return signalStoreFeature(
    withMethods(store => {
      function clearStorage(clearStore = true): void {
        const state = getState(store);
        let toClear: Record<string, unknown> | Partial<typeof state>;
        if (config.select) {
          toClear = config.select(state);
        } else if (config.props) {
          toClear = config.props.reduce((acc, prop) => ({ ...acc, [prop]: state[prop] }), {});
        } else {
          toClear = state;
        }
        // clear relevant keys from storage
        for (const key of Object.keys(toClear)) {
          const storageKey = `${config.prefix}_${key}`;
          config.storage.removeItem(storageKey);
          toClear[key] = undefined;
        }
        if (clearStore) {
          patchState(store, toClear);
        }
      }
      function persistStorage(): void {
        const state = getState(store);
        let toSerialize: Record<string, unknown> | Partial<typeof state>;
        if (config.select) {
          toSerialize = config.select(state);
        } else if (config.props) {
          toSerialize = config.props.reduce((acc, prop) => ({ ...acc, [prop]: state[prop] }), {});
        } else {
          toSerialize = state;
        }
        for (const [key, value] of Object.entries(toSerialize)) {
          const storageKey = `${config.prefix}_${key}`;
          if (value === undefined || value === null) {
            config.storage.removeItem(storageKey);
          } else {
            config.storage.setItem(storageKey, JSON.stringify(value));
          }
        }
      }
      function restoreStorage(): void {
        const state = getState(store);
        let toRestore: Record<string, unknown> | Partial<typeof state>;
        if (config.select) {
          toRestore = config.select(state);
        } else if (config.props) {
          toRestore = config.props.reduce((acc, prop) => ({ ...acc, [prop]: state[prop] }), {});
        } else {
          toRestore = state;
        }
        const restoredData: Partial<typeof state> = {};
        for (const key of Object.keys(toRestore)) {
          const storageKey = `${config.prefix}_${key}`;
          const item = config.storage.getItem(storageKey);
          if (item !== null) {
            try {
              restoredData[key] = JSON.parse(item);
            } catch {
              // ignore JSON parse errors to avoid breaking the app on unexpected data through updates
            }
          }
        }
        if (config.parse) {
          const parsedData = config.parse(restoredData);
          patchState(store, parsedData);
        } else {
          patchState(store, restoredData);
        }
      }
      return { clearStorage, persistStorage, restoreStorage };
    }),
    withHooks({
      onInit: store => {
        store.restoreStorage();
        watchState(store, () => {
          store.persistStorage();
        });
      },
    }),
  );
}
